import { atom, useAtomValue } from "jotai";
import * as z from "zod";

import { default as MessageDialog } from "src/components/MessageDialog/MessageDialog";
import { Checkbox } from "src/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { ADVISORY_ADDONS } from "src/lib/advisory";
import { SEND_CONTACT_MESSAGE_MUTATION } from "src/lib/queries/contact";

export const contactDialogAtom = atom({
  open: false,
  prefillValues: undefined,
  focusField: undefined,
  variant: undefined,
});

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters",
  }),
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
  message: z
    .string()
    .min(10, {
      message: "Message must be at least 10 characters",
    })
    .max(4000, {
      message: "Message is too long",
    }),
  requestedAddons: z.array(z.string()).optional(),
  storeUrl: z
    .string()
    .max(500, { message: "Store URL is too long" })
    .optional(),
});

const ContactDialog = () => {
  const dialogState = useAtomValue(contactDialogAtom);
  const isAdvisory = dialogState.variant === "advisory";

  return (
    <MessageDialog
      formSchema={formSchema}
      defaultValues={{
        email: "",
        name: "",
        message: "",
        requestedAddons: [],
        storeUrl: "",
      }}
      submitMapping={(data) => ({
        input: {
          ...data,
          advisoryInquiry: isAdvisory,
          requestedAddons: isAdvisory ? data.requestedAddons || [] : [],
          storeUrl: isAdvisory ? data.storeUrl?.trim() || null : null,
        },
      })}
      atom={contactDialogAtom}
      mutation={SEND_CONTACT_MESSAGE_MUTATION}
      title={isAdvisory ? "Request Lucris Advisory" : "Contact Us"}
      infoBoxText={
        isAdvisory
          ? "Tell us which services you are interested in. We will review your needs and follow up before preparing a quote."
          : `Have a question or want to learn more about ${process.env.APP_NAME}? Send us a message and we'll get back to you shortly.`
      }
    >
      {({ form, loading, submitted }) => (
        <>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            disabled={loading || submitted}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="jane@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  We&apos;ll only use this to respond to your message.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
            disabled={loading || submitted}
          />

          {isAdvisory ? (
            <>
              <FormField
                control={form.control}
                name="storeUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="your-store.myshopify.com"
                        autoComplete="url"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      This helps us connect your inquiry to the right store.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
                disabled={loading || submitted}
              />

              <FormField
                control={form.control}
                name="requestedAddons"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Optional services</FormLabel>
                    <div className="space-y-3 pt-1">
                      {ADVISORY_ADDONS.map(([value, label]) => (
                        <FormItem
                          key={value}
                          className="flex flex-row items-start gap-2 space-y-0"
                        >
                          <FormControl>
                            <Checkbox
                              className="rounded-xs"
                              checked={(field.value || []).includes(value)}
                              onCheckedChange={(checked) =>
                                field.onChange(
                                  checked
                                    ? Array.from(
                                        new Set([...(field.value || []), value])
                                      )
                                    : (field.value || []).filter(
                                        (item) => item !== value
                                      )
                                )
                              }
                            />
                          </FormControl>
                          <FormLabel className="font-normal">{label}</FormLabel>
                        </FormItem>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
                disabled={loading || submitted}
              />
            </>
          ) : null}

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {isAdvisory ? "What should we know?" : "Your Message"}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={
                      isAdvisory
                        ? "Tell us about your goals, reporting needs, or custom systems."
                        : "Tell us how we can help"
                    }
                    className="min-h-[150px]"
                    maxLength={4000}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            disabled={loading || submitted}
          />
        </>
      )}
    </MessageDialog>
  );
};

export default ContactDialog;
